import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useMemo, useRef } from 'react';
import en from 'date-fns/locale/en-GB';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import Icons from '../../../Elements/Icons';
import { YearSelect, MonthSelect, RangePresets } from '../components';
import { DatePickerInput } from '../components/DatePickerInput';
import { DATE_FORMAT, KEY_DATE_FORMAT, KEY_ENTER, CALENDAR_CLASSNAME, CALENDAR_PORTAL_ID, DATE_PICKER_REF, DATE_RANGE_PLACEHOLDER, DATE_PICKER_RANGE_INPUT_MIN_WIDTH, DEFAULT_DATE_PICKER_RANGE_TRANSLATIONS, } from '../constants';
import { getDateArray, getValidDateRange, isDefinedDateRange, toLocalDisplayDate } from '../helpers';
import { usePreventCalendarCloseOnTouch } from '../hooks';
import { DatePickerStyles, DatePickerHeaderWrapper } from '../styled';
export const DatePickerRange = memo(forwardRef(function DatePickerRange({ dataTestId, dateFormat = [DATE_FORMAT, DATE_FORMAT], dateFormatKey = KEY_DATE_FORMAT, rangePresets, startDate, endDate, showLocal, onChange, onBlur, onKeyDown, value, error, warning, locale = 'en', isClearable, openToEndDate, minDate, maxDate, translations, ...props }, ref) {
    const { colors } = useTheme();
    // setPreSelection is a real react-datepicker instance method but is missing from @types/react-datepicker.
    const datePickerRef = useRef(null);
    registerLocale(locale, en);
    const { handleCalendarOpen, handleCalendarClose } = usePreventCalendarCloseOnTouch();
    const mergedTranslations = useMemo(() => ({
        ...DEFAULT_DATE_PICKER_RANGE_TRANSLATIONS,
        ...translations,
    }), [translations]);
    const [selectedStartDate, selectedEndDate] = useMemo(() => {
        if (!value) {
            return [startDate, endDate];
        }
        return getValidDateRange({ value, dateFormatKey, showLocal, minDate, maxDate }) ?? [startDate, endDate];
    }, [value, startDate, endDate, dateFormatKey, showLocal, minDate, maxDate]);
    const handleKeyDown = useCallbackRef((e) => {
        if (onKeyDown) {
            onKeyDown(e);
        }
        else if (e.key === KEY_ENTER && e.target instanceof HTMLInputElement) {
            const range = getValidDateRange({
                value: e.currentTarget.value,
                dateFormatKey,
                showLocal,
                minDate,
                maxDate,
            });
            if (range) {
                onChange(range, e);
            }
        }
    });
    const handleBlur = useCallbackRef((e) => {
        const range = getValidDateRange({
            value: e.target.value,
            dateFormatKey,
            showLocal,
            minDate,
            maxDate,
        });
        if (range)
            onChange(range, e);
        onBlur?.(e);
    });
    const onClear = useCallbackRef(() => {
        onChange([null, null], undefined);
    });
    const handleRangeChange = useCallbackRef((value, e) => {
        onChange(value, e);
        datePickerRef.current?.setOpen(false);
    });
    useEffect(() => {
        if (rangePresets && !startDate && !endDate) {
            const defaultRange = rangePresets.find((item) => item.default === true);
            if (defaultRange?.dateRange && isDefinedDateRange(defaultRange.dateRange)) {
                onChange(defaultRange.dateRange, undefined);
            }
        }
    }, [rangePresets, startDate, endDate, onChange]);
    const wrapOnChange = useCallbackRef((value, e) => {
        const range = getDateArray(value, showLocal);
        onChange(range, e);
    });
    const { selectedEndDateFormatted, selectedStartDateFormatted } = useMemo(() => ({
        selectedStartDateFormatted: selectedStartDate ? toLocalDisplayDate(selectedStartDate, showLocal) : null,
        selectedEndDateFormatted: selectedEndDate ? toLocalDisplayDate(selectedEndDate, showLocal) : null,
    }), [selectedEndDate, selectedStartDate, showLocal]);
    const handleOpen = useCallbackRef(() => {
        handleCalendarOpen();
        if (openToEndDate && selectedEndDateFormatted) {
            datePickerRef.current?.setPreSelection(selectedEndDateFormatted);
        }
    });
    return (_jsxs(_Fragment, { children: [_jsx(DatePickerStyles, {}), _jsx(ReactDatePicker, { popperClassName: CALENDAR_CLASSNAME, customInputRef: DATE_PICKER_REF, customInput: _jsx(DatePickerInput, { minWidth: DATE_PICKER_RANGE_INPUT_MIN_WIDTH, dataTestId: dataTestId, icon: isClearable && (!!startDate || !!endDate) ? (_jsx(Icons.Clear, { filled: true, size: 20, color: colors.grey70_1, onClick: onClear, dataTestId: 'icons-clear' })) : (_jsx(Icons.Calendar, { size: 20, color: colors.grey100_1 })), error: error, warning: warning, isActionIcon: isClearable && (!!startDate || !!endDate), ref: ref }), renderCustomHeader: ({ date, changeYear, changeMonth }) => (_jsxs(DatePickerHeaderWrapper, { children: [_jsx(MonthSelect, { date: date, changeMonth: changeMonth, changeYear: changeYear, locale: locale, dataTestId: dataTestId }), _jsx(YearSelect, { date: date, changeYear: changeYear, minYear: minDate?.getFullYear(), maxYear: maxDate?.getFullYear(), dataTestId: dataTestId })] })), value: value, onChange: wrapOnChange, startDate: selectedStartDateFormatted, endDate: selectedEndDateFormatted, dateFormat: dateFormat, locale: locale, onKeyDown: handleKeyDown, onBlur: handleBlur, selectsRange: true, placeholderText: DATE_RANGE_PLACEHOLDER, portalId: CALENDAR_PORTAL_ID, minDate: minDate, maxDate: maxDate, onCalendarOpen: handleOpen, onCalendarClose: handleCalendarClose, ref: datePickerRef, ...props, children: rangePresets && (_jsx(RangePresets, { presets: rangePresets, onChange: handleRangeChange, startDate: startDate, endDate: endDate, translations: mergedTranslations })) })] }));
}));
